// Small geometry helpers for WGS84 polygons returned by SAPPA.
// Pure, zero-dep, usable in both client and Worker runtimes.

const R_EARTH = 6371008.8; // mean Earth radius in metres

export function haversineMetres(a, b) {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const toRad = Math.PI / 180;
  const φ1 = lat1 * toRad;
  const φ2 = lat2 * toRad;
  const dφ = (lat2 - lat1) * toRad;
  const dλ = (lon2 - lon1) * toRad;
  const s =
    Math.sin(dφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(s));
}

// Edge lengths (metres) of the outer ring of a GeoJSON Polygon.
// Returns [] for an invalid polygon.
export function edgeLengths(geojsonPolygon) {
  const outer = geojsonPolygon?.coordinates?.[0];
  if (!outer || outer.length < 2) return [];
  const lens = [];
  for (let i = 0; i < outer.length - 1; i++) {
    lens.push(haversineMetres(outer[i], outer[i + 1]));
  }
  return lens;
}

// Rough frontage estimate. For a roughly rectangular parcel, frontage is the
// shorter of the two perpendicular edges. Heuristic: take the shortest edge
// longer than 3m (filters out survey noise / tiny chamfers).
//
// Returns { estimate, confidence } where confidence is:
//   'high'    — polygon is ~4 sides with one edge clearly shortest
//   'medium'  — polygon has 4–6 sides
//   'low'     — irregular polygon, result is a rough floor
export function estimateFrontage(geojsonPolygon) {
  const lens = edgeLengths(geojsonPolygon);
  if (!lens.length) return null;
  const significant = lens.filter((l) => l > 3);
  if (!significant.length) return null;
  const sorted = [...significant].sort((a, b) => a - b);
  const shortest = sorted[0];
  const secondShortest = sorted[1] ?? shortest;

  let confidence;
  if (lens.length <= 4) confidence = 'high';
  else if (lens.length <= 6) confidence = 'medium';
  else confidence = 'low';

  return {
    estimate: Math.round(shortest * 10) / 10,
    secondShortest: Math.round(secondShortest * 10) / 10,
    sides: lens.length,
    confidence,
  };
}

// Centroid of the outer ring — simple average of vertices. Good enough for
// framing a map view; not the true area-weighted centroid.
export function polygonCentroid(geojsonPolygon) {
  const outer = geojsonPolygon?.coordinates?.[0];
  if (!outer || outer.length === 0) return null;
  let sx = 0, sy = 0, n = 0;
  // Last vertex often duplicates the first in GeoJSON closed rings; skip dup.
  const last = outer.length - 1;
  const skipLast =
    outer[0][0] === outer[last][0] && outer[0][1] === outer[last][1];
  const limit = skipLast ? last : outer.length;
  for (let i = 0; i < limit; i++) {
    sx += outer[i][0];
    sy += outer[i][1];
    n++;
  }
  return n > 0 ? [sx / n, sy / n] : null;
}

// Spherical-excess area of a GeoJSON Polygon (lon/lat) in m². Equivalent
// to turf.js `area()` for a single polygon — uses the same Karney formula.
//
// This matters because SAPPA's `st_area(shape)` is computed in EPSG:3857
// (Web Mercator) and at Adelaide's latitude that inflates reported areas
// by ~1.49×. Trust the polygon geometry, not the server-computed area.
const R_EARTH_AREA = 6378137; // WGS84 equatorial radius, used by Esri/turf

export function polygonAreaM2(geojsonPolygon) {
  const rings = geojsonPolygon?.coordinates;
  if (!rings || rings.length === 0) return 0;
  // outer - holes
  let total = ringArea(rings[0]);
  for (let i = 1; i < rings.length; i++) total -= Math.abs(ringArea(rings[i]));
  return Math.abs(total);
}

function ringArea(ring) {
  if (!ring || ring.length < 3) return 0;
  let total = 0;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [lonA, latA] = ring[i];
    const [lonB, latB] = ring[(i + 1) % n];
    total += toRad(lonB - lonA) *
      (2 + Math.sin(toRad(latA)) + Math.sin(toRad(latB)));
  }
  return (total * R_EARTH_AREA * R_EARTH_AREA) / 2;
}

function toRad(deg) { return (deg * Math.PI) / 180; }

// Perimeter in metres — sum of Haversine edge lengths.
export function polygonPerimeterM(geojsonPolygon) {
  return edgeLengths(geojsonPolygon).reduce((a, b) => a + b, 0);
}

// Bounding box [[south, west], [north, east]] in Leaflet's lat-lng order.
export function polygonBoundsLatLng(geojsonPolygon) {
  const outer = geojsonPolygon?.coordinates?.[0];
  if (!outer || outer.length === 0) return null;
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const [lon, lat] of outer) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  return [[minLat, minLon], [maxLat, maxLon]];
}
